/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import '@/components/header.css';
import HeaderNav from '@/components/HeaderNav';
import PageComponent from '@/components/PageComponent';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { freApi } from '@/store/apis';
import { actions, fetchFlowByProductId, fetchNavigationData, fetchRunData, fetchSubNavigationData } from '@/store/Products';
import * as storageUtils from '@/utils/storageUtils';
import { validateUUIDv4 } from '@/utils/utils';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const Dashboard = () => {
    const productList = useAppSelector(state => state?.productList);
    const { flowId, phaseId, stepId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { currentPhase, currentStep, currentFlowId } = productList;
    const [getProductByFlowId] = freApi.useLazyGetProductByFlowIdQuery();

    useEffect(() => {
        if (!currentFlowId || !currentPhase.id || !currentStep.id) return;

        navigate({
            pathname: `/dashboard/flow/${currentFlowId}/phase/${currentPhase.id}/step/${currentStep.id}`,
            search: location.search,
        });
    }, [currentFlowId, currentPhase.id, currentStep.id]);

    useEffect(() => {
        (async () => {
            if (!storageUtils.getItem('user')) {
                navigate('/login');
                return;
            }
            if (!productList.currentSKU?.SKUId) {
                loadPersonalizationData();
            }
        })();
    }, []);

    const loadPersonalizationData = async () => {
        if (!validateUUIDv4(flowId) || !validateUUIDv4(phaseId) || !validateUUIDv4(stepId)) {
            navigate('/');
            return;
        }

        try {
            const product = await getProductByFlowId(flowId).unwrap();
            dispatch(
                actions.setCurrentProduct({
                    productName: product.name,
                })
            );
            dispatch(
                actions.setCurrentSKU({
                    SKUId: product.sku,
                })
            );

            const flowData = await dispatch(fetchFlowByProductId({ productId: product.id }));
            const fetchFlowId = flowData.payload.id;
            const phasesData = await dispatch(fetchNavigationData({ flowId: fetchFlowId }));
            let currentPhase: any = null;
            if (phasesData?.payload?.length > 0) {
                phasesData.payload.forEach((phase: any) => {
                    if (phase.id === phaseId) {
                        currentPhase = phase;
                        dispatch(
                            actions.setCurrentPhase({
                                name: phase.name,
                                id: phase.id,
                                displayOrder: phase.displayOrder,
                                translationNameKey: phase.translationNameKey,
                            })
                        );
                    }
                });
            } else {
                // phase Not found
                navigate('/');
                return;
            }
            const stepsData = await dispatch(fetchSubNavigationData({ phaseId: currentPhase.id }));
            if (stepsData.payload.length > 0) {
                stepsData.payload.forEach((step: any) => {
                    if (step.id === stepId) {
                        dispatch(actions.setCurrentStep(step.id));
                    }
                });
            } else {
                navigate('/');
                return;
            }
            try {
                await dispatch(
                    fetchRunData({
                        flow: flowData.payload,
                    })
                );
            } catch {
                navigate('/');
                return;
            }
        } catch {
            navigate('/');
        }
    };

    return (
        <>
            <HeaderNav title={productList.currentProduct.productName} realm={productList.currentRealm} />
            <PageComponent />
        </>
    );
};

export default Dashboard;
